import { useHudStore } from "../../store/hudStore";
import { warmthColor, sinceText } from "../../data/seed";
import { bloom, fill } from "../ui";
import type { Person } from "../../types";

// Relationships module: warmth by how long it's been (app.html peopleModule, lines 1073–1091).
//
// Two things this page must never do: claim contact it has no record of, and
// judge a client contact by the cadence you hold your mother to. Hence
// `days === null` as a state of its own, and the two circles below.

// Personal first. It is the circle you would actually regret letting go quiet.
//
// Each circle brings its own vocabulary for logging. "We spent time" under a
// heart is the right words for your mother and the wrong words entirely on a
// card that reads "previous manager, eCloudvalley PH", and the same goes for
// calling a client call a "moment". Same mechanic either way, +25 XP to Bonds.
type Circle = { key: string; label: string; fa: string; log: string; logFa: string; noun: string };
const CIRCLES: Circle[] = [
  { key: "personal", label: "Personal", fa: "fa-solid fa-people-roof", log: "We spent time", logFa: "fa-solid fa-heart", noun: "moment" },
  { key: "professional", label: "Professional", fa: "fa-solid fa-briefcase", log: "We talked", logFa: "fa-solid fa-comment-dots", noun: "conversation" },
];

// Who needs you, in order: genuinely overdue, then never logged, then the
// people you are current with. Never-logged cannot outrank a real overdue
// count, because no record of contact is not the same as a record of silence.
const tier = (p: Person): number => (p.days === null ? 1 : p.days > p.cadence ? 0 : 2);
const ratio = (p: Person): number => (p.days === null ? 0 : p.days / p.cadence);
// Inside a tier: furthest past cadence first, and among the never-logged the
// tightest cadence first, since that is the one you meant to keep closest.
// Name last, so ten people who are equally unlogged hold a stable order rather
// than reshuffling on every fetch.
const byNeed = (a: Person, b: Person): number =>
  tier(a) - tier(b) || ratio(b) - ratio(a) || a.cadence - b.cadence || a.name.localeCompare(b.name);

function PersonCard({ p, c, onLog }: { p: Person; c: Circle; onLog: () => void }) {
  const d = p.days;
  // Nothing logged means there is no ratio to compute: no bar to fill and no
  // percentage to quote. A neutral hatched track says "unknown" where a full
  // green one used to say "perfect", which was the whole bug.
  const r = d === null ? 0 : Math.max(0, Math.min(1, 1 - d / (p.cadence * 3)));
  const wc = d === null ? "var(--mut)" : warmthColor(r);
  const over = d !== null && d > p.cadence;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 16, borderRadius: 10, background: "var(--card2)", border: "1px solid var(--line)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
        <span style={{ width: 38, height: 38, flex: "0 0 auto", borderRadius: 999, display: "grid", placeItems: "center", fontSize: 15, fontWeight: 700, color: "var(--on-accent)", background: p.color }}>{p.name.charAt(0)}</span>
        <span style={{ minWidth: 0 }}>
          <span style={{ display: "block", fontSize: 14, fontWeight: 700, fontFamily: "var(--display)", letterSpacing: "0" }}>{p.name}</span>
          <span style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", marginTop: 3, color: p.color }}>{p.tie}</span>
        </span>
      </div>
      <div>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: d === null ? "var(--mut)" : "var(--ink)" }}>{sinceText(d)}</span>
          {d !== null && <span style={{ fontSize: 11, fontWeight: 700, color: wc }}>{Math.round(r * 100)}%</span>}
        </div>
        <div
          style={{
            height: 7,
            borderRadius: 999,
            overflow: "hidden",
            marginTop: 8,
            // Hatched, not empty: an empty track reads as zero warmth, and zero
            // is a measurement. This is the absence of one.
            background: d === null ? "repeating-linear-gradient(90deg,var(--raise) 0 5px,transparent 5px 10px)" : "var(--raise)",
          }}
        >
          {d !== null && <span style={{ ...fill(Math.round(r * 100), 0.7), borderRadius: 999, background: wc, boxShadow: bloom(wc, r * 100, 10) }} />}
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, marginTop: 7, color: over ? "var(--orange-ink)" : "var(--mut)" }}>
          {d === null
            ? "no " + c.noun + " logged against your " + p.cadence + "-day cadence"
            : over
              ? "past your own cadence of " + p.cadence + " days"
              : "within your cadence"}
        </div>
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--mut)", lineHeight: 1.45, textWrap: "pretty" }}>{p.next}</div>
      <button type="button" onClick={onLog} style={{ marginTop: "auto", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 12, fontWeight: 700, color: "var(--accent-ink)", background: "var(--raise)", border: "1px solid var(--control-edge)", padding: "10px 14px", borderRadius: 8, cursor: "pointer" }}>
        <i className={c.logFa} style={{ fontSize: 11, lineHeight: 1 }} />{c.log}
      </button>
    </div>
  );
}

export default function PeopleModule() {
  const st = useHudStore();
  const sorted = st.people.slice().sort(byNeed);
  const c = sorted[0];
  const blanks = sorted.filter((p) => p.days === null).length;

  // The headline names the single most pressing thing, and says nothing it
  // cannot back. With no overdue count and nothing logged, the honest headline
  // is that there is nothing to go on, not that everyone is warm.
  let coldestLine = "";
  let coldestNote = "";
  if (c && c.days !== null && c.days > c.cadence) {
    coldestLine = "It has been " + c.days + " days since " + c.name + ".";
    coldestNote = c.next || "Past your cadence of " + c.cadence + " days, with no next moment planned.";
  } else if (c && blanks) {
    coldestLine = blanks === 1 ? "Nothing logged yet for " + c.name + "." : "Nothing logged yet for " + blanks + " of them.";
    coldestNote = "Not a record of silence, just no record at all. Log a moment and this page can start telling you something true.";
  } else if (c) {
    coldestLine = "Everyone's warm right now.";
    coldestNote = "Nothing overdue, enjoy it.";
  }

  return (
    <div style={{ background: "var(--card)", border: "1px solid var(--line)", borderRadius: 10, padding: "20px clamp(16px,2vw,22px) 22px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--mut)" }}>Who needs you</div>
          <div style={{ fontSize: "clamp(20px,3vw,26px)", fontWeight: 700, fontFamily: "var(--display)", letterSpacing: "-0.01em", lineHeight: 1.2, marginTop: 10, textWrap: "pretty" }}>{coldestLine}</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--mut)", marginTop: 7, textWrap: "pretty" }}>{coldestNote}</div>
        </div>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 11.5, fontWeight: 700, color: "var(--mut)", background: "var(--card2)", border: "1px solid var(--line)", padding: "8px 13px", borderRadius: 999 }}>
          {/* Elapsed time, not affection: this pill speaks for both circles, so
              it takes the muted mark the circle headings use rather than a
              pink heart, which only ever described the personal half. */}
          <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 11, lineHeight: 1 }} />no scores, no streaks: just how long it's been
        </span>
      </div>

      {CIRCLES.map((circle) => {
        const { key, label, fa } = circle;
        const group = sorted.filter((p) => p.circle === key);
        if (!group.length) return null;
        const over = group.filter((p) => p.days !== null && p.days > p.cadence).length;
        const none = group.filter((p) => p.days === null).length;
        return (
          <div key={key} style={{ marginTop: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <i className={fa} style={{ fontSize: 11, lineHeight: 1, color: "var(--mut)" }} />
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--mut)" }}>{label}</span>
              <span style={{ flex: 1, height: 1, minWidth: 16, background: "var(--line)" }} />
              {over > 0 && <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--orange-ink)" }}>{over} overdue</span>}
              {none > 0 && <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--mut)" }}>{none} not logged</span>}
              {!over && !none && <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--mut)" }}>all within cadence</span>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 12, marginTop: 12 }}>
              {group.map((p) => (
                <PersonCard key={p.id} p={p} c={circle} onLog={() => st.logMoment(p.id)} />
              ))}
            </div>
          </div>
        );
      })}

      <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--mut)", marginTop: 16, textWrap: "pretty" }}>Cadence is yours to set, not a target to beat. It only decides when this page gently raises its hand, and it is judged inside each circle rather than across both. A card with nothing logged is not one you are on top of, it is one this page cannot speak for yet. Logging a moment or a conversation gives +25 XP to Bonds.</div>
    </div>
  );
}
